import {
  BASE_VERSION,
  IDubTask,
  IFileData
} from "@/features/clientProjectDetails";
import { DUB_STATUS } from "@/types/project";
import useDesktopNotifications from "./useDesktopNotifications";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { MIXPANEL_EVENTS } from "@/constants/mixpanel";
import { getMixpanelLanguageName, trackMixpanelEvent } from "@/utils/mixpanel";
import useSWR from "swr";
import { projectDetailsApi } from "@/features/clientProjectDetails/api";
import { useMemo, useRef } from "react";
import { updateSourceFileResponseList } from "@/reducers/slices/currentClientProjectSlice/currentClientProjectSlice";
import { notificationSlice } from "@/reducers/slices/notificationSlice/notificationSlice";

enum NOTIFICATION_SENT_FOR {
  NONE = "NONE",
  DUB_READY = "DUB_READY",
  ALL_DUBS_READY = "ALL_DUBS_READY"
}

export const useDubNotifications = () => {
  const navigate = useNavigate();
  // const location = useLocation();
  const dispatch = useAppDispatch();
  const { showNotification } = useDesktopNotifications();
  const activeWorkspaceId = useTypedSelector(
    (state) => state.user.activeWorkspaceId
  );
  const dubsToPoll = useTypedSelector(
    (state) => state.notificationHandler?.dubsToPoll
  );
  const currentClientProject = useTypedSelector(
    (state) => state.currentClientProject
  );

  const isNotificationSent = useRef(NOTIFICATION_SENT_FOR.NONE);

  const { projectId, fileIds, interval, projectName } = useMemo(() => {
    let projectId = undefined;
    let fileIds = undefined;
    let interval = 0;
    let projectName = undefined;
    if (dubsToPoll?.length) {
      interval = 1000 * 30;
      projectId = dubsToPoll[0]?.projectId;
      fileIds = dubsToPoll[0]?.sourceFileIds;
      projectName = dubsToPoll[0]?.projectName;
    }

    return {
      projectId,
      fileIds,
      interval,
      projectName
    };
  }, [dubsToPoll]);

  const [searchParams] = useSearchParams();

  const navigateToPreviewPage = (dub: IDubTask, projectId: string) => {
    const { dubId, sourceFileId, dubVersion, locale } = dub;
    trackMixpanelEvent(MIXPANEL_EVENTS.OPEN_DUB, {
      Language: getMixpanelLanguageName(locale)
    });
    navigate(
      `/project/${projectId}/file/${sourceFileId}/dub/${dubId}/preview?${searchParams.toString()}&dubVersion=${
        dubVersion || BASE_VERSION
      }`
    );
  };

  const handleDubNotification = (filesData: IFileData[], projectId: string) => {
    const hasCompletedDub = (filesData as IFileData[])
      .filter((file) =>
        file.dubResponseList?.some(
          (dub) => dub.dubStatus === DUB_STATUS.COMPLETED
        )
      )
      .map((file) =>
        file.dubResponseList?.find(
          (dub) => dub.dubStatus === DUB_STATUS.COMPLETED
        )
      )
      .filter(Boolean) as unknown as IDubTask[];

    const hasCompletedAllDubs = Boolean(
      (filesData as IFileData[])
        .filter((file) =>
          file.dubResponseList?.every(
            (dub) => dub.dubStatus === DUB_STATUS.COMPLETED
          )
        )
        .map((file) =>
          file.dubResponseList?.find(
            (dub) => dub.dubStatus === DUB_STATUS.COMPLETED
          )
        )
        .filter(Boolean)?.length
    );

    if (hasCompletedDub?.length) {
      if (
        isNotificationSent.current === NOTIFICATION_SENT_FOR.NONE ||
        (hasCompletedAllDubs &&
          isNotificationSent.current === NOTIFICATION_SENT_FOR.DUB_READY)
      ) {
        showNotification({
          title: "MurfDub",
          body: hasCompletedAllDubs
            ? `All your dubs are ready for ${projectName}. Preview your dubs now.`
            : `Your first dub is ready for ${projectName}. Preview your dub now.`,
          onClick: () => {
            trackMixpanelEvent(MIXPANEL_EVENTS.NOTIFICATION_CLICKED, {
              EntryPoint: "Dub Ready"
            });
            if (hasCompletedAllDubs) {
              navigate(
                `/project/${projectId}?workspaceId=${activeWorkspaceId}`
              );
            } else {
              navigateToPreviewPage(hasCompletedDub[0], projectId);
            }
          },
          tag: projectId
        });
        isNotificationSent.current = hasCompletedAllDubs
          ? NOTIFICATION_SENT_FOR.ALL_DUBS_READY
          : NOTIFICATION_SENT_FOR.DUB_READY;
        dispatch(
          notificationSlice.actions.removeDubsToPoll({
            projectId,
            sourceFileIds: hasCompletedAllDubs ? undefined : fileIds
          })
        );
        isNotificationSent.current = NOTIFICATION_SENT_FOR.NONE;
      }
    }
  };

  useSWR(
    projectId && fileIds?.length
      ? `internal/projects/${projectId}/all-dub-status/${fileIds.join(",")}`
      : null,
    () => {
      if (!projectId || !fileIds?.length) {
        return;
      }
      return projectDetailsApi.fetchDubStatus({ projectId, fileIds });
    },
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      refreshInterval: interval,
      onSuccess: (res) => {
        if (projectId && res?.data?.responseData?.length) {
          handleDubNotification(res.data.responseData, projectId);

          if (currentClientProject?.[projectId]) {
            dispatch(
              updateSourceFileResponseList({
                projectId,
                sourceFileResponseList: res.data.responseData
              })
            );
          }
        }
      },
      isPaused: () => {
        return !dubsToPoll?.length;
      }
    }
  );

  // setTimeout(() => {

  //   showNotification({
  //     title: "MurfDub",
  //     body:
  //       1 + 1 === 2
  //         ? `All your dubs are ready for ${projectName}. Preview your dubs now.`
  //         : `Your first dub is ready for ${projectName}. Preview your dub now.`,
  //     onClick: () => {
  //       // trackMixpanelEvent(MIXPANEL_EVENTS.NOTIFICATION_CLICKED, {
  //       //   EntryPoint: "Dub Ready"
  //       // });
  //     },
  //     tag: projectId
  //   });

  // }, 1000 * 1);

  return {
    handleDubNotification
  };
};
